import type { CacheId } from "../io/rangeCache";
import { HttpRangeSource, BlobRangeSource, type RangeSource } from "../io/rangeSource";
import type { CoreApi, TcItem } from "../tc/coreApi";

/** Where a dataset came from, kept for the info panel and for re-signing download URLs. */
export interface DatasetSource {
  label: string;
  kind: "connect" | "local" | "url";
  detail: string;
  bytes: number;
  /** Absent for bundles assembled from several loose files, which have no single stream. */
  range?: RangeSource;
  /** Trimble Connect item id, for a source loaded from Connect — used to mark it "loaded" in the browser. */
  id?: string;
  /**
   * Identity used to key the on-disk range/tile-location cache — stable across re-signed
   * download URLs, unlike `range`. Absent for local files, which need no caching.
   */
  cache?: CacheId;
}

export async function sourceFromConnectFile(api: CoreApi, item: TcItem): Promise<DatasetSource> {
  const cache: CacheId = { group: `connect:${item.id}`, key: `connect:${item.id}:${item.versionId ?? "latest"}` };
  const range = new HttpRangeSource(item.name, () => api.getDownloadUrl(item.id, item.versionId), { cache });
  const bytes = item.size ?? (await range.size());
  return {
    label: item.name,
    kind: "connect",
    detail: "Trimble Connect project file",
    bytes,
    range,
    id: item.id,
    cache,
  };
}

export function sourceFromFile(file: File): DatasetSource {
  return {
    label: file.name,
    kind: "local",
    detail: "Local file",
    bytes: file.size,
    range: new BlobRangeSource(file.name, file),
  };
}

export async function sourceFromUrl(url: string): Promise<DatasetSource> {
  const label = decodeURIComponent(url.split("?")[0].split("/").pop() || url);
  const cache: CacheId = { group: `url:${url}`, key: `url:${url}` };
  const range = new HttpRangeSource(label, async () => url, { cache });
  const bytes = await range.size();
  return { label, kind: "url", detail: url, bytes, range, cache };
}
